import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChatCircleDots, X, PaperPlaneTilt } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { useBeta } from "../context/BetaContext";
import { brandName } from "../brand";

const CATEGORIES = [
  ["bug", "Something is broken"],
  ["confusing", "This was confusing"],
  ["gameplay", "Gameplay feedback"],
  ["crew", "Crew & progression"],
  ["difficulty", "Difficulty / balance"],
  ["performance", "Performance / speed"],
  ["pwa_install", "Install / home screen"],
  ["accessibility", "Accessibility"],
  ["other", "Something else"],
];

const SENTIMENTS = [
  ["positive", "Positive"],
  ["neutral", "Neutral"],
  ["negative", "Negative"],
];

export default function FeedbackModal() {
  const { feedbackOpen, closeFeedback } = useBeta();
  const loc = useLocation();
  const [category, setCategory] = useState("confusing");
  const [sentiment, setSentiment] = useState("neutral");
  const [message, setMessage] = useState("");
  const [mayContact, setMayContact] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!feedbackOpen) return null;

  const reset = () => {
    setCategory("confusing");
    setSentiment("neutral");
    setMessage("");
    setMayContact(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Please add a short note first");
    setBusy(true);
    try {
      await api.post("/beta/feedback", {
        category,
        sentiment,
        message: message.trim(),
        route: loc.pathname,
        may_contact: mayContact,
        device: (navigator.userAgent || "").slice(0, 160),
      });
      toast.success("Thank you — your feedback reached the team.");
      reset();
      closeFeedback();
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("You're sending feedback too quickly. Please wait a moment.");
      } else {
        toast.error(apiErr(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-end justify-center bg-black/75 p-0 sm:items-center sm:p-4" data-testid="feedback-modal">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="ff-panel w-full max-w-md rounded-t-lg p-6 sm:rounded-md"
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ff-gold">
            <ChatCircleDots size={22} weight="duotone" /> Send Feedback
          </h2>
          <button onClick={closeFeedback} data-testid="feedback-close-btn" className="text-ff-muted hover:text-ff-gold">
            <X size={20} />
          </button>
        </div>
        <p className="mb-4 text-xs text-ff-muted">
          You're in the {brandName} private beta. Tell us what's confusing, broken, or worth improving.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-widest text-ff-brass">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="feedback-category"
              className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2.5 text-sm text-ff-parchment focus:border-ff-gold focus:outline-none"
            >
              {CATEGORIES.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-widest text-ff-brass">Overall feeling</label>
            <div className="flex gap-2" data-testid="feedback-sentiment">
              {SENTIMENTS.map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setSentiment(v)}
                  data-testid={`feedback-sentiment-${v}`}
                  className={`flex-1 rounded-sm border py-2 text-xs transition-colors ${
                    sentiment === v ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted hover:text-ff-parchment"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-widest text-ff-brass">Your note</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
              rows={4}
              maxLength={2000}
              placeholder="What happened, and what did you expect?"
              data-testid="feedback-message"
              className="w-full resize-none rounded-sm border border-ff-brass/30 bg-ff-bg p-2.5 text-sm text-ff-parchment placeholder:text-ff-muted/60 focus:border-ff-gold focus:outline-none"
            />
            <div className="mt-0.5 text-right text-[10px] text-ff-muted">{message.length}/2000</div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-xs text-ff-muted" data-testid="feedback-may-contact">
            <input type="checkbox" checked={mayContact} onChange={() => setMayContact((v) => !v)} className="h-4 w-4 accent-ff-gold" />
            The team may contact me about this feedback.
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="feedback-submit-btn"
            className="flex w-full items-center justify-center gap-2 rounded-sm bg-ff-gold py-3 font-semibold text-ff-bg transition-all hover:bg-ff-brass active:scale-95 disabled:opacity-60"
          >
            <PaperPlaneTilt size={16} weight="fill" /> {busy ? "Sending..." : "Send Feedback"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
